<script setup>
import { computed } from 'vue'
import { Link } from '@inertiajs/vue3'
import { Button } from '@/Components/ui/button'
import { Radio, UserRound, CheckCircle2 } from 'lucide-vue-next'
import { tutors } from '@/routes/index'
import { formatMoney } from '@/lib/money'

/**
 * The core of the offer: two ways to learn, side by side, with the same card
 * skeleton so they read as one product with two shapes. Violet marks Bindle Live
 * and nothing else.
 *
 * The 1:1 price is the cheapest weekly rate any listed tutor charges, read from
 * the database. It was hard-coded at £50 while the real floor was £4 — a figure
 * nobody can notice is wrong until a customer reaches the listing.
 *
 * Bindle Live has no product behind it yet, so its action is disabled and says
 * so. Its price is the intended launch price and the card is labelled as such.
 */
const props = defineProps({
  figures: {
    type: Object,
    default: () => ({}),
  },
})

const oneToOneFrom = computed(() => props.figures?.one_to_one_from ?? null)
</script>

<template>
  <section id="how-it-works" class="scroll-mt-24 pb-14 md:pb-20">
    <div class="container-page">
      <div class="rounded-2xl border border-line bg-surface-2 p-6 md:p-10">
        <div class="grid gap-8 md:grid-cols-[1fr_auto_1fr] md:gap-6">
          <!-- Bindle Live -->
          <article class="flex gap-4">
            <span class="grid size-13 shrink-0 place-items-center rounded-full bg-live-soft text-live-soft-text">
              <Radio class="size-6" />
            </span>
            <div>
              <div class="flex flex-wrap items-center gap-2">
                <h2 class="font-sans text-lg font-bold uppercase tracking-[0.1em] text-live-soft-text">
                  Bindle Live
                </h2>
                <span class="rounded-full bg-live-soft px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-live-soft-text">
                  Launching soon
                </span>
              </div>
              <p class="mt-3 text-[15px] leading-relaxed text-muted">
                Live masterclasses every week with outstanding tutors. Join live or watch anytime with
                recordings and notes included.
              </p>
              <p class="mt-5 text-sm text-muted">
                Planned from
                <strong class="font-display text-3xl font-bold text-live-soft-text">£1.99</strong> /week
              </p>
              <Button variant="live" size="sm" class="mt-4" disabled aria-disabled="true">
                Coming soon!
              </Button>
            </div>
          </article>

          <!-- Divider -->
          <div class="flex items-center gap-4 md:flex-col">
            <span class="h-px flex-1 bg-line md:h-auto md:w-px"></span>
            <span class="grid size-11 shrink-0 place-items-center rounded-full bg-brand text-[11px] font-extrabold text-brand-contrast">
              OR
            </span>
            <span class="h-px flex-1 bg-line md:h-auto md:w-px"></span>
          </div>

          <!-- 1:1 -->
          <article class="flex gap-4">
            <span class="grid size-13 shrink-0 place-items-center rounded-full bg-brand-soft text-brand-soft-text">
              <UserRound class="size-6" />
            </span>
            <div>
              <h2 class="font-sans text-lg font-bold uppercase tracking-[0.1em] text-brand-soft-text">
                1:1 Tutoring
              </h2>
              <p class="mt-3 text-[15px] leading-relaxed text-muted">
                One-to-one lessons tailored to your child’s needs and goals. Personalised support whenever
                they need extra help.
              </p>
              <p v-if="oneToOneFrom" class="mt-5 text-sm text-muted">
                From
                <strong class="font-display text-3xl font-bold text-brand">{{ formatMoney(oneToOneFrom) }}</strong> /week
              </p>
              <p v-else class="mt-5 text-sm text-muted">
                Each tutor sets their own weekly price.
              </p>
              <Link :href="tutors()" class="mt-4 inline-block">
                <Button variant="secondary" size="sm">Find your tutor</Button>
              </Link>
            </div>
          </article>
        </div>

        <p class="mt-8 flex items-center justify-center gap-2 rounded-md bg-bg py-3 text-center text-sm font-semibold text-ink">
          <CheckCircle2 class="size-4 text-brand" />
          Two ways to learn. One goal: better results.
        </p>
      </div>
    </div>
  </section>
</template>
